import { useState } from "react";
import { toast } from "sonner";
import axios from "axios";
import { Mail, MessageCircle, MapPin, Clock, Send, Loader2, CheckCircle2, Instagram, Youtube, Facebook } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { COMPANY, SERVICE_OPTIONS, openWhatsApp } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Contact = () => {
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", service: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (key) => (e) => setForm({ ...form, [key]: e?.target ? e.target.value : e });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.service || !form.message) {
      toast.error("Please fill all required fields.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/contact`, form);
      toast.success(data.message || "Enquiry received. We'll reach out shortly.");
      setDone(true);
      setForm({ name: "", company: "", email: "", phone: "", service: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again or WhatsApp us.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="py-24 lg:py-32 relative overflow-hidden" data-testid="contact-section">
      <div className="absolute inset-0 mesh-bg opacity-70" aria-hidden />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-5 gap-10">
        {/* Left: Info */}
        <div className="lg:col-span-2 space-y-8">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-brand-purple">Contact</div>
            <h2 className="section-heading text-3xl sm:text-4xl lg:text-5xl mt-3 text-slate-900">
              Let&apos;s build something <span className="text-gradient">great</span> together.
            </h2>
            <p className="mt-4 text-slate-600 leading-relaxed">
              Tell us about your goals — we&apos;ll respond within 24 hours with a tailored plan and quote.
            </p>
          </div>

          <div className="space-y-4">
            <a href={`mailto:${COMPANY.email}`} className="flex items-center gap-4 p-4 bg-white border border-slate-100 rounded-2xl card-lift" data-testid="contact-email-card">
              <div className="w-11 h-11 rounded-xl bg-brand-gradient-soft flex items-center justify-center text-brand-purple">
                <Mail className="w-5 h-5" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-slate-500">Email</div>
                <div className="font-semibold text-slate-900">{COMPANY.email}</div>
              </div>
            </a>
            <a href={COMPANY.whatsappUrl} target="_blank" rel="noopener noreferrer" onClick={openWhatsApp} className="flex items-center gap-4 p-4 bg-white border border-slate-100 rounded-2xl card-lift" data-testid="contact-whatsapp-card">
              <div className="w-11 h-11 rounded-xl bg-emerald-50 flex items-center justify-center text-brand-whatsapp">
                <MessageCircle className="w-5 h-5" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-slate-500">WhatsApp</div>
                <div className="font-semibold text-slate-900">{COMPANY.whatsappNumber}</div>
              </div>
            </a>
            <div className="flex items-center gap-4 p-4 bg-white border border-slate-100 rounded-2xl">
              <div className="w-11 h-11 rounded-xl bg-brand-gradient-soft flex items-center justify-center text-brand-purple">
                <Clock className="w-5 h-5" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-slate-500">Business Hours</div>
                <div className="font-semibold text-slate-900">Mon – Sat · 9:00 AM – 8:00 PM IST</div>
              </div>
            </div>
            <div className="flex items-center gap-4 p-4 bg-white border border-slate-100 rounded-2xl">
              <div className="w-11 h-11 rounded-xl bg-brand-gradient-soft flex items-center justify-center text-brand-purple">
                <MapPin className="w-5 h-5" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-slate-500">Location</div>
                <div className="font-semibold text-slate-900">Remote-first · Serving clients worldwide</div>
              </div>
            </div>
            <div className="flex items-center gap-3 p-4 bg-white border border-slate-100 rounded-2xl" data-testid="contact-socials">
              <span className="text-xs uppercase tracking-widest text-slate-500 mr-2">Follow us</span>
              <a
                href={COMPANY.socials.facebook}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
                data-testid="contact-social-facebook"
                className="w-10 h-10 flex items-center justify-center rounded-full bg-slate-50 text-slate-700 hover:bg-brand-gradient hover:text-white transition-all"
              >
                <Facebook className="w-4 h-4" />
              </a>
              <a
                href={COMPANY.socials.instagram}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                data-testid="contact-social-instagram"
                className="w-10 h-10 flex items-center justify-center rounded-full bg-slate-50 text-slate-700 hover:bg-brand-gradient hover:text-white transition-all"
              >
                <Instagram className="w-4 h-4" />
              </a>
              <a
                href={COMPANY.socials.youtube}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="YouTube"
                data-testid="contact-social-youtube"
                className="w-10 h-10 flex items-center justify-center rounded-full bg-slate-50 text-slate-700 hover:bg-brand-gradient hover:text-white transition-all"
              >
                <Youtube className="w-4 h-4" />
              </a>
            </div>
          </div>

          <div className="rounded-2xl overflow-hidden border border-slate-100 soft-shadow bg-slate-50 p-8 text-center" data-testid="contact-global-note">
            <div className="text-brand-purple flex justify-center">
              <MapPin className="w-8 h-8" />
            </div>
            <div className="mt-3 font-heading font-bold text-slate-900">Remote-first · Global delivery</div>
            <div className="mt-1 text-sm text-slate-600">
              We collaborate over WhatsApp, email and video calls in your timezone — clients across India, MENA, EU, US and APAC.
            </div>
          </div>
        </div>

        {/* Right: Form */}
        <div className="lg:col-span-3">
          <div className="bg-white rounded-3xl border border-slate-100 soft-shadow-lg p-8 md:p-10" data-testid="contact-form-card">
            {done ? (
              <div className="text-center py-10" data-testid="contact-success">
                <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600">
                  <CheckCircle2 className="w-8 h-8" />
                </div>
                <h3 className="mt-6 font-heading text-2xl font-bold text-slate-900">Message received!</h3>
                <p className="mt-2 text-slate-600">Thanks for reaching out. Our team will get back to you within 24 hours.</p>
                <button
                  className="mt-6 text-sm font-semibold text-brand-purple hover:underline"
                  onClick={() => setDone(false)}
                  data-testid="contact-send-another"
                >
                  Send another enquiry
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <Label htmlFor="name">Name *</Label>
                    <Input id="name" data-testid="contact-input-name" value={form.name} onChange={update("name")} placeholder="Your full name" className="mt-1.5 h-11" />
                  </div>
                  <div>
                    <Label htmlFor="company">Company</Label>
                    <Input id="company" data-testid="contact-input-company" value={form.company} onChange={update("company")} placeholder="Company name (optional)" className="mt-1.5 h-11" />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input id="email" type="email" data-testid="contact-input-email" value={form.email} onChange={update("email")} placeholder="you@company.com" className="mt-1.5 h-11" />
                  </div>
                  <div>
                    <Label htmlFor="phone">Phone *</Label>
                    <Input id="phone" data-testid="contact-input-phone" value={form.phone} onChange={update("phone")} placeholder="+91 98765 43210" className="mt-1.5 h-11" />
                  </div>
                </div>

                <div>
                  <Label>Service Required *</Label>
                  <Select value={form.service} onValueChange={update("service")}>
                    <SelectTrigger className="mt-1.5 h-11" data-testid="contact-select-service">
                      <SelectValue placeholder="Select a service" />
                    </SelectTrigger>
                    <SelectContent>
                      {SERVICE_OPTIONS.map((s) => (
                        <SelectItem key={s} value={s} data-testid={`contact-option-${s.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}>
                          {s}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="message">Message *</Label>
                  <Textarea
                    id="message"
                    data-testid="contact-input-message"
                    value={form.message}
                    onChange={update("message")}
                    placeholder="Tell us about your project, goals, timelines…"
                    rows={5}
                    className="mt-1.5"
                  />
                </div>

                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 pt-2">
                  <Button
                    type="submit"
                    disabled={loading}
                    className="btn-gradient rounded-full h-12 px-8 font-semibold text-white"
                    data-testid="contact-submit-btn"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                    {loading ? "Sending…" : "Send Enquiry"}
                  </Button>
                  <a
                    href={COMPANY.whatsappUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={openWhatsApp}
                    className="rounded-full h-12 px-6 inline-flex items-center justify-center bg-brand-whatsapp text-white font-semibold hover:-translate-y-0.5 transition-transform"
                    data-testid="contact-whatsapp-btn"
                  >
                    <MessageCircle className="w-4 h-4 mr-2" /> Or WhatsApp us
                  </a>
                </div>

                <p className="text-xs text-slate-500 pt-2">
                  By submitting this form you agree to be contacted about your enquiry. We respect your privacy.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
